import { map, publishLast, refCount } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { Attribute } from 'src/app/shared/models/attributes/attribute';
import { AttributeWrite } from 'src/app/shared/models/attributes/attributeWrite';
import { Injectable } from '@angular/core';
import { BaseService, getParsedError } from 'src/app/shared/services/base.service';
import { ApiVersions } from 'src/app/shared/services/api-version';
import { ComplexAttributeWrite } from '../models/attributes/complexAttributeWrite';

@Injectable({
  providedIn: 'root',
})
export class CustomFieldsService extends BaseService {
  constructor() {
    super();
  }

  getAttributes(personId: string): Observable<Attribute[]> {
    return this.get(this.getPersonAttributesUrl(personId), null, ApiVersions.v1_0)
      .pipe(map(this.convertAttributesData))
      .pipe(publishLast())
      .pipe(refCount());
  }

  updateGroupAttributes(
    personId: string,
    groupId: string,
    attributes: AttributeWrite[]
  ): Observable<Attribute[]> {
    let body = JSON.stringify(attributes);
    let path = this.getPersonAttributeGroupUrl(personId, groupId);

    return this.post(path, body, null, ApiVersions.v1_0)
      .pipe(map(this.convertAttributesData))
      .pipe(publishLast())
      .pipe(refCount());
  }

  /** returns and array of length 1+ on success. returns null on failure. Handles error snack on error. Callee should display any success snack message */
  updateAttribute = async (
    personId: string,
    attributeDefinitionId: string,
    attribute: AttributeWrite
  ): Promise<Attribute[]> => {
    try {
      const result = await this.post(
        this.getPersonAttributeUrl(personId, attributeDefinitionId),
        JSON.stringify(attribute),
        null,
        ApiVersions.v1_0,
        true
      )
        .pipe(map(this.convertAttributesData))
        .pipe(publishLast())
        .pipe(refCount())
        .toPromise();
      if (result.length) {
        return result; //happy path
      }
      this.openSnackBar('unexpected response from updateAttribute', false);
    } catch (error) {
      const parsedMessage = getParsedError(error);

      const errorMessage =
        parsedMessage.isGenericError && error.message ? error.message : parsedMessage.message;

      this.openSnackBar(errorMessage, false);
    }
    return null; //error condition.
  };

  addOrUpdateComplexAttribute(
    personId: string,
    attributeDefinitionId: string,
    attributes: ComplexAttributeWrite[]
  ): Observable<Attribute[]> {
    let body = JSON.stringify(attributes);
    return this.post(
      this.getPersonComplexAttributeUrl(personId, attributeDefinitionId),
      body,
      null,
      ApiVersions.v1_0
    )
      .pipe(map(this.convertAttributesData))
      .pipe(publishLast())
      .pipe(refCount());
  }

  deleteAttribute(personId: string, attributeDefinitionId: string): Observable<any> {
    return this.delete(
      this.getPersonAttributeUrl(personId, attributeDefinitionId),
      null,
      ApiVersions.v1_0
    )
      .pipe(publishLast())
      .pipe(refCount());
  }

  private getPersonAttributesUrl(personId: string): string {
    return `user/${personId}/custom-fields`;
  }

  private getPersonAttributeUrl(personId: string, attributeDefinitionId: string): string {
    return `user/${personId}/custom-fields/${attributeDefinitionId}`;
  }

  private getPersonComplexAttributeUrl(personId: string, attributeDefinitionId: string): string {
    return `user/${personId}/custom-fields/${attributeDefinitionId}/complex`;
  }

  private getPersonAttributeGroupUrl(personId: string, groupId: string): string {
    return `user/${personId}/custom-fields/group/${groupId}`;
  }

  private convertAttributesData(data): Array<Attribute> {
    let attributes: Array<Attribute> = [];
    return data ? data.map((a) => Attribute.parse(a)) : attributes;
  }
}
